import moment from "moment";
import PersonPinIcon from "@mui/icons-material/PersonPin";

export const TOKEN_NAME = "token";
export const USER_SESSION_LANDED = "user_session_landed";
/** Set before navigating to LOGIN_ROUTE; blocks duplicate AuthnRequest creation. */
export const SAML_AUTHN_STARTED = "saml_authn_started";
export const LOGIN_ROUTE = `${window.location.origin}/saml2/authenticate/oamfed`;

/**
 * Set once a credential login succeeds. The SSO gate treats it like a return
 * from SAML: validate with /user rather than hand off to OAM. Without it the
 * gate finds no session on the post-login reload and redirects to SAML,
 * undoing the login that just happened.
 */
export const CREDENTIAL_SESSION = "credential_session";

/**
 * Query param that swaps the SAML hand-off for an employeeId/password form:
 * /?internallogin=true. Read off the router's searchParams so it re-evaluates
 * on navigation.
 *
 * Runtime rather than build-time, so one image serves every environment and
 * toggling it needs no rebuild. The trade-off is that anyone can set it, in any
 * environment — this only decides which UI renders. /internal/session/create
 * must be disabled server-side outside dev; that is the actual control, and
 * without it this param is an MFA-free way in.
 */
export const INTERNAL_LOGIN_PARAM = "internallogin";

/**
 * Where the credential-login paths send users when they need the form back
 * (logout, retry-after-logout). Carries the param explicitly because nothing
 * else persists the choice once the URL that set it is gone.
 */
export const INTERNAL_LOGIN_ROUTE = `/?${INTERNAL_LOGIN_PARAM}=true`;

export const RETAILER_DASHBOARD_PAGE_SIZE = 10;

// REGEX
export const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@ril.com$/;

export const HTTP_METHOD = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
};

export const USER_BASED_MENU_ITEMS = [
  { label: "File upload", value: "admin/file/upload" },
  {
    label: "Configure business",
    value: "admin/business/create",
  },
  {
    label: "Configure Role",
    value: "admin/role/create",
  },
  {
    label: "Configure dashboard groups",
    value: "admin/dashboardgroup/create",
  },
  { label: "Configure dashboards", value: "admin/dashboard/create" },
  { label: "Configure reports", value: "admin/report/create" },
  { label: "Configure charts", value: "admin/chart/create" },
  { label: "Configure filters", value: "admin/filter/create" },
  { label: "Configure access", value: "admin/access/manage" },
  { label: "Configure users", value: "admin/user/manage" },
];

export const logoutUrls = {
  dev: `https://sitohs.jio.com:4443/oam/server/logout?end_url=`,
  uat: `https://replogin.jiolabs.com/oam/server/logout?end_url=`,
  prod: `https://login.jioconnect.com/oam/server/logout?end_url=`,
};

export const dummy_schema = {
  tables: [
    {
      name: "orders",
      columns: [
        {
          name: "id",
          type: "INTEGER",
          isPrimaryKey: true,
          isNullable: false,
        },
        {
          name: "customer_id",
          type: "INTEGER",
          isNullable: false,
          isForeignKey: true,
          references: {
            table: "customers",
            column: "id",
          },
        },
        {
          name: "amount",
          type: "DECIMAL",
          isNullable: false,
        },
        {
          name: "status",
          type: "ENUM",
          isNullable: false,
          enumValues: ["pending", "shipped", "delivered"],
        },
        {
          name: "order_date",
          type: "DATE",
          isNullable: false,
        },
      ],
    },
    {
      name: "customers",
      columns: [
        {
          name: "id",
          type: "INTEGER",
          isPrimaryKey: true,
          isNullable: false,
        },
        {
          name: "name",
          type: "VARCHAR",
          isNullable: false,
        },
        {
          name: "email",
          type: "VARCHAR",
          isNullable: true,
        },
      ],
    },
  ],
  relationships: [
    {
      fromTable: "orders",
      fromColumn: "customer_id",
      toTable: "customers",
      toColumn: "id",
      type: "INNER",
    },
  ],
};

export const operators = [
  "Equals",
  "Not Equals",
  "Greater Than",
  "Less Than",
  "Contains",
  "Starts With",
];

export const DIGITAL_FILTERS = {
  location_filter: {
    regions: [
      {
        id: "R01",
        name: "West",
        clusters: [
          {
            id: "C01",
            name: "Mumbai",
            stores: [
              {
                id: "S001",
                name: "Store Malad",
              },
              {
                id: "S002",
                name: "Store Andheri",
              },
            ],
          },
          {
            id: "C02",
            name: "Pune",
            stores: [],
          },
        ],
      },
      {
        id: "R02",
        name: "South",
        clusters: [],
      },
    ],
  },
  product_filter: {
    families: [
      {
        id: "F01",
        name: "Electronics",
        brands: [
          {
            id: "B01",
            name: "Samsung",
          },
          {
            id: "B02",
            name: "Sony",
          },
        ],
      },
      {
        id: "F02",
        name: "Appliances",
        brands: [],
      },
    ],
  },
};

export const metricData = [
  {
    title: "PMI (Profit Margin Index)",
    actual: 234.3,
    target: 308.65,
    percent: 76.4,
  },
  {
    title: "Sales",
    actual: 208.3,
    target: 231,
    percent: 90.1,
  },
  {
    title: "LFL sales",
    actual: 190.2,
    target: 200,
    percent: 95.1,
  },
  {
    title: "Discounting",
    actual: 8.2,
    target: 8.6,
    percent: 95.34,
  },
];

export const API_RAW_DATA = [
  {
    billing_date: "2025-06-01",
    target_date: "2025-06-01",
    site_id: "S001",
    family_id: "F01",
    brand_id: "B01",
    sales_target: 1200000,
    total_sales: 1150000,
    margin_target: 300000,
    total_margin: 285000,
    lfl_sales: 980000,
    discount_target: 50000,
    total_discount: 52000,
  },
  {
    billing_date: "2025-07-01",
    region_id: "R01",
    cluster_id: "C01",
    site_id: "S002",
    family_id: "F02",
    brand_id: "B03",
    sales_target: 800000,
    total_sales: 850000,
    margin_target: 200000,
    total_margin: 215000,
    lfl_sales: 750000,
    discount_target: 30000,
    total_discount: 27000,
  },
];

export const performanceData = [
  {
    region: "North 2",
    pmi: 241.6,
    pmiPercent: "80.86%",
    sales: "₹36.5L",
    salesPercent: "92.15%",
    lfl: "94.30%",
    discount: "2.15%",
    stores: 5,
  },
  {
    region: "West",
    pmi: 268.0,
    pmiPercent: "80.80%",
    sales: "₹34.1L",
    salesPercent: "88.97%",
    lfl: "90.75%",
    discount: "5.49%",
    stores: 6,
  },
  {
    region: "Central",
    pmi: 216.4,
    pmiPercent: "70.03%",
    sales: "₹25.4L",
    salesPercent: "88.62%",
    lfl: "88.45%",
    discount: "5.02%",
    stores: 5,
  },
  {
    region: "South 2",
    pmi: 170.67,
    pmiPercent: "65.39%",
    sales: "₹10.4L",
    salesPercent: "87.95%",
    lfl: "89.14%",
    discount: "9.65%",
    stores: 3,
  },
  {
    region: "South 1",
    pmi: 286.0,
    pmiPercent: "81.09%",
    sales: "₹32.9L",
    salesPercent: "93.19%",
    lfl: "95.05%",
    discount: "3.55%",
    stores: 7,
  },
  {
    region: "East",
    pmi: 215.67,
    pmiPercent: "75.17%",
    sales: "₹36.3L",
    salesPercent: "90.71%",
    lfl: "90.71%",
    discount: "2.48%",
    stores: 8,
  },
  {
    region: "South 3",
    pmi: 187.0,
    pmiPercent: "68.41%",
    sales: "₹19.8L",
    salesPercent: "91.01%",
    lfl: "92.91%",
    discount: "4.11%",
    stores: 3,
  },
  {
    region: "North 1",
    pmi: 229.0,
    pmiPercent: "79.10%",
    sales: "₹12.8L",
    salesPercent: "90.00%",
    lfl: "92.62%",
    discount: "2.48%",
    stores: 1,
  },
];

export const productFamilyData = [
  {
    name: "Air Conditioners",
    pmi: 205.6,
    pmiPercent: "72.3%",
    sales: "₹15.2L",
    salesPercent: 85,
    lfl: 92.4,
    discount: 3.2,
    tag: "Hero",
    children: [
      {
        name: "Brand A",
        pmi: 210.1,
        pmiPercent: "74.2%",
        sales: "₹8.7L",
        salesPercent: 88,
        lfl: 90.1,
        discount: 2.8,
      },
      {
        name: "Brand B",
        pmi: 198.0,
        pmiPercent: "70.4%",
        sales: "₹6.5L",
        salesPercent: 82,
        lfl: 94.3,
        discount: 3.6,
      },
    ],
  },
  {
    name: "Refrigerators",
    pmi: 189.3,
    pmiPercent: "68.9%",
    sales: "₹12.4L",
    salesPercent: 79,
    lfl: 87.2,
    discount: 4.1,
  },
];

export const brandMetricData = {
  primary_sales: [
    {
      revenue_lacs: {
        title: "Revenue (Lacs)",
        value: 6207,
        details: [
          [
            { level: "State", name: "Maharashtra", value: 1250 },
            { level: "Area", name: "Mumbai Metro", value: 680 },
            { level: "Distributor", name: "WIRELESS PHONE", value: 340 },
            { level: "Retailer", name: "Mobile World", value: 120 },
            { level: "IMEI", name: "860123456789012", value: 1 },
          ],
        ],
      },
    },
    {
      units_sold: {
        title: "Units Sold",
        value: 71404,
        details: [
          [
            { level: "State", name: "Karnataka", value: 20000 },
            { level: "Area", name: "Bangalore Central", value: 10000 },
            { level: "Distributor", name: "SPEED COMM", value: 6000 },
            { level: "Retailer", name: "Mobile Mart", value: 2500 },
            { level: "IMEI", name: "860123456789099", value: 1 },
          ],
        ],
      },
    },
    {
      overall_distributor: {
        title: "Overall Distributor",
        value: 114,
        details: [],
      },
    },
    {
      participating_distributor: {
        title: "Participating Distributor",
        value: 113,
        details: [],
      },
    },
    {
      balance_inventory: {
        title: "Balance Inventory",
        value: 7764,
        details: [],
      },
    },
  ],
  secondary_sales: [
    {
      revenue_lacs: {
        title: "Revenue (Lacs)",
        value: 5568,
        details: [
          [
            { level: "State", name: "West Bengal", value: 1800 },
            { level: "Area", name: "Kolkata East", value: 1200 },
            { level: "Retailer", name: "Smart Zone", value: 212.3 },
          ],
        ],
      },
    },
    {
      units_sold: {
        title: "Units Sold",
        value: 63640,
        details: [
          [
            { level: "State", name: "Gujarat", value: 15640 },
            { level: "Area", name: "Ahmedabad Urban", value: 9000 },
            { level: "Retailer", name: "Digital Mobile Store", value: 240 },
          ],
        ],
      },
    },
    {
      retailer_count: {
        title: "Retailer Count",
        value: 10470,
        details: [],
      },
    },
  ],
  tertiary_sales: [
    {
      revenue_lacs: {
        title: "Revenue (Lacs)",
        value: 5125,
        details: [],
      },
    },
    {
      activated_device: {
        title: "Activated Device",
        value: 60119,
        details: [
          [
            { level: "State", name: "Delhi NCR", value: 15200 },
            { level: "Area", name: "South Delhi", value: 6800 },
            { level: "Retailer", name: "Tech Gallery", value: 2 },
            { level: "IMEI", name: "860999123456789", value: 1 },
            { level: "IMEI", name: "860999123456790", value: 1 },
          ],
        ],
      },
    },
    {
      activation_ratio: {
        title: "Activation Ratio",
        value: 84.2,
        details: [
          [
            { level: "State", name: "Tamil Nadu", value: 84.8 },
            { level: "Area", name: "Chennai North", value: 83.9 },
            {
              level: "Retailer",
              name: "Digital India",
              activated: 212,
              sold: 250,
              ratio: "84.8%",
            },
          ],
        ],
      },
    },
  ],
};
